import { __ } from '@wordpress/i18n';
import { Card } from '@getdokan/dokan-ui';
import { CustomerInfo } from '../../types/quote';
import { dateI18n, getSettings } from '@wordpress/date';

interface ShippingDetailsProps {
    customerInfo: CustomerInfo;
    expectedDate?: string;
}

const ShippingDetails = ( {
    customerInfo,
    expectedDate,
}: ShippingDetailsProps ) => {
    return (
        <Card className={ `dokan-layout rounded-lg shadow overflow-hidden` }>
            <Card.Header className="bg-[#f8f8f8]">
                <Card.Title className="m-0">
                    <div className="flex items-center gap-3">
                        <svg
                            className="h-7 w-7"
                            viewBox="0 0 28 28"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-semibold">
                                { __( 'Shipping Details', 'dokan' ) }
                            </h3>
                            <p className="text-sm text-gray-500 font-normal">
                                { __(
                                    '(The information needs to assume your distance from our shop)',
                                    'dokan'
                                ) }
                            </p>
                        </div>
                    </div>
                </Card.Title>
            </Card.Header>
            <Card.Body className="bg-[#fdfdfd]">
                <div className="space-y-4">
                    <div className="space-y-4">
                        { ( customerInfo.country ||
                            customerInfo.state_address ) && (
                            <div className="flex items-start gap-3">
                                <span className="text-black-400 mt-1">
                                    <svg
                                        className="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                </span>
                                <div className="font-medium">
                                    { customerInfo.country && (
                                        <div className="country-info">
                                            <span className="text-black">
                                                { __( 'Country: ', 'dokan' ) }
                                            </span>
                                            <span className="font-normal">
                                                { customerInfo.country }
                                            </span>
                                        </div>
                                    ) }

                                    { customerInfo.state_address && (
                                        <div className="state-info">
                                            <span className="text-black">
                                                { __( 'State: ', 'dokan' ) }
                                            </span>
                                            <span className="font-normal">
                                                { customerInfo.state_address }
                                            </span>
                                        </div>
                                    ) }
                                </div>
                            </div>
                        ) }

                        { customerInfo.city && (
                            <div className="flex items-start gap-3 font-medium">
                                <span className="text-black-400 mt-1">
                                    <svg
                                        className="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect
                                            x="4"
                                            y="2"
                                            width="16"
                                            height="20"
                                            rx="2"
                                            ry="2"
                                        />
                                        <path d="M9 22v-4h6v4" />
                                        <path d="M8 6h.01" />
                                        <path d="M16 6h.01" />
                                        <path d="M12 6h.01" />
                                        <path d="M12 10h.01" />
                                        <path d="M12 14h.01" />
                                        <path d="M16 10h.01" />
                                        <path d="M16 14h.01" />
                                        <path d="M8 10h.01" />
                                        <path d="M8 14h.01" />
                                    </svg>
                                </span>
                                <div className="city-info">
                                    <span className="text-black">
                                        { __( 'City: ', 'dokan' ) }
                                    </span>
                                    <span className="font-normal">
                                        { customerInfo.city }
                                    </span>
                                </div>
                            </div>
                        ) }

                        { ( customerInfo.addr_line_1 ||
                            customerInfo.addr_line_2 ) && (
                            <div className="flex items-start gap-3">
                                <span className="text-black-400 mt-1">
                                    <svg
                                        className="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                </span>
                                <div className="font-medium">
                                    { customerInfo.addr_line_1 && (
                                        <div className="address-line-info">
                                            <span className="text-black">
                                                { __(
                                                    'Address Line 1: ',
                                                    'dokan'
                                                ) }
                                            </span>
                                            <span className="font-normal">
                                                { customerInfo.addr_line_1 }
                                            </span>
                                        </div>
                                    ) }

                                    { customerInfo.addr_line_2 && (
                                        <div className="address-line-info">
                                            <span className="text-black">
                                                { __(
                                                    'Address Line 2: ',
                                                    'dokan'
                                                ) }
                                            </span>
                                            <span className="font-normal">
                                                { customerInfo.addr_line_2 }
                                            </span>
                                        </div>
                                    ) }
                                </div>
                            </div>
                        ) }
                    </div>

                    { expectedDate && (
                        <div className="flex items-center gap-3 text-gray-600 mt-4">
                            <span className="text-black-400">
                                <svg
                                    className="h-5 w-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="18"
                                        rx="2"
                                        ry="2"
                                    />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </span>
                            <div className="expected-date-info font-medium">
                                <span className="text-black">
                                    { __(
                                        'Expected Shipping Date: ',
                                        'dokan'
                                    ) }
                                </span>
                                <span className="font-normal">
                                    { dateI18n(
                                        getSettings().formats.date,
                                        expectedDate,
                                        getSettings().timezone.string
                                    ) }
                                </span>
                            </div>
                        </div>
                    ) }
                </div>
            </Card.Body>
        </Card>
    );
};

export default ShippingDetails;
